import { pick, uniq, values, pickBy } from 'lodash';
import { createSelector, ParametricSelector } from 'reselect';
import { State } from 'reducers';
import { getUsers } from 'selectors/usersSelectors';
import { getThemes } from 'selectors/themesSelectors';
import { getDeviceTypes } from 'selectors/deviceTypesSelectors';
import { AugmentedBuilding, BuildingId, DeviceId, Theme, ThemeId } from 'shared/models';
import { initialState } from 'reducers/buildingsReducer';


export type HasUrlSlug = {
    urlSlug: string;
};
export type HasBuildingId = {
    buildingId: BuildingId;
};
export type HasDeviceId = {
    deviceId: DeviceId;
};
export type HasThemeId = {
    themeId: ThemeId;
};
export type HasDevicePage = {
    devicePage: 'status' | 'schedule' | 'events' | 'commands';
};

const getBuildingId = (state: State, { buildingId }: Partial<HasBuildingId>) => buildingId;
const getUrlSlug = (state: State, { urlSlug }: Partial<HasUrlSlug>) => urlSlug;
export const getDeviceId = (state: State, { deviceId }: HasDeviceId) => deviceId;
//const getThemeId = (state: State, { themeId }: HasThemeId) => themeId;

const buildingUrlSlugRegex = /^\/buildings\/(.*?)(?:\/|$)/i;
const deviceUrlSlugRegex = /\/devices\/(.*?)(?:\/|$)/i;

export const getCurrentBuildingUrlSlug =
    (state: State) => {
        const pathname = state.router.location.pathname || '';
        const match = buildingUrlSlugRegex.exec(pathname);
        return match ? match[1] : null;
    };

export const getCurrentDeviceId =
    (state: State) => {
        const pathname = state.router.location.pathname || '';
        const match = deviceUrlSlugRegex.exec(pathname);
        if (!match) return null;
        const deviceId = match[1] || null;
        if (deviceId === 'new') return null;
        return deviceId;
    };

const getBuildingsState =
    (state: State) =>
        (state || initialState).buildings;

export const getBuildings =
    createSelector(getBuildingsState,
        state => state.data
    );

export const getCurrentBuildingCountry =
    createSelector(
        getBuildings,
        getCurrentBuildingUrlSlug,
        (buildings, urlSlug) => {
            if (!buildings) return '';
            const building = values(buildings).find(b => b.urlSlug === urlSlug);
            if (!building) return '';
            return building.country || 'US';
        }
    );

// TODO: could change this to take into account how long ago buildings were loaded
export const buildingsLoadedSelector =
    (state: State) =>
        state.buildings.loaded;

export const getBuilding: ParametricSelector<State, Partial<HasUrlSlug & HasBuildingId>, AugmentedBuilding | null> =
    createSelector(
        getBuildings,
        getBuildingId,
        getUrlSlug,
        (buildings, id, urlSlug) => {
            if (!buildings) return null;
            return (id ? buildings[id] : values(buildings).find(b => b.urlSlug === urlSlug)) || null;
        }
    );

export const getBuildingByUrlSlug =
    createSelector(
        getBuildings,
        getUrlSlug,
        (buildings, urlSlug) => {
            if (!buildings) return null;
            return values(buildings).find(b => b.urlSlug === urlSlug || b.id === urlSlug) || null;
        }
    );

export const getBuildingIdByUrlSlug =
    createSelector(getBuildingByUrlSlug,
        building => building && building.id
    );

export const getBuildingsError =
    (state: State) =>
        state.buildings.error;

export const getIsLoadingBuilding =
    createSelector(
        getBuildingsState,
        getUrlSlug,
        getBuildingId,
        (buildings, urlSlug, buildingId) => !!(buildings && (urlSlug || buildingId) && buildings.loading[urlSlug || buildingId!])
    );

export const isLoadingBuildings =
    createSelector(getBuildingsState,
        state => state && !!state.loading['all']
    );

export const getLastSearch =
    createSelector(getBuildingsState,
        state => state && state.lastSearch
    );

export const getLastSearchOrder =
    createSelector(getLastSearch,
        state => state ? state.order : []
    );

export const getLastSearchMore =
    createSelector(getLastSearch,
        state => state && state.more
    );

export const getBuildingUsers =
    createSelector(
        getBuilding,
        getUsers,
        (building, users) => {
            if (!building || !users) return {};
            const userIds = building.userIds || [];
            if (userIds.length === 0) {
                return {};
            }
            return pick(users, ...userIds);
        }
    );

export const getBuildingThemes =
    createSelector(
        getBuilding,
        getThemes,
        (building, themes) => {
            if (!building || !themes) return null;
            const { themeIds } = building;
            if (!themeIds) return null;
            return pick(themes, ...themeIds) as Record<ThemeId, Theme>;
        }
    );

export const getIsLoadingBuildingThemes =
    createSelector(
        getBuildingsState,
        getBuildingId,
        (state, id) => !!state.loadingThemes[id!]
    );

export const getLoadingBuildingThemesError =
    createSelector(
        getBuildingsState,
        getBuildingId,
        (state, buildingId) => state.themesError[buildingId!] || null
    );

export const getBuildingDevices =
    createSelector(getBuilding,
        building => (building && building.devices) || null
    );

export const getBuildingDevice =
    createSelector(
        getBuildingDevices,
        getDeviceId,
        (devices, deviceId) => (devices && deviceId && devices[deviceId]) || null
    );

const getBuildingDevicePins =
    createSelector(getBuilding,
        building => building && building.devicePins
    );

const getBuildingDevicePinState =
    createSelector(
        getBuildingDevicePins,
        getDeviceId,
        (pins, deviceId) => (deviceId && pins && pins[deviceId]) || null
    );

export const getBuildingDevicePin =
    createSelector(getBuildingDevicePinState,
        state => state && state.pin
    );

export const getBuildingDevicePinPending =
    createSelector(getBuildingDevicePinState,
        state => !!(state && state.pending)
    );

export const getBuildingDevicePinError =
    createSelector(getBuildingDevicePinState,
        state => state && state.error
    );

export const getBuildingDeviceTypes =
    createSelector(
        getBuildingDevices,
        getDeviceTypes,
        (devices, deviceTypes) => {
            if (!devices || !deviceTypes) return {};
            const buildingDeviceTypeIds = uniq(values(devices).map(d => d.deviceTypeId));
            return pick(deviceTypes, buildingDeviceTypeIds);
        }
    );


export const getBuildingDeviceGroupNames =
    createSelector(getBuildingDevices,
        devices => {
            if (!devices) return [];
            return uniq(
                values(devices)
                    .map(d => d.groupName!)
                    .filter(s => s)
            )
                .sort((a, b) => {
                    if (!a) return -1;
                    if (!b) return 1;
                    return a.localeCompare(b);
                });
        }
    );

export const getBuildingCredentials =
    createSelector(getBuilding,
        building => building ? building.credentials : []
    );

const getBuildingCredentialsOriginal =
    createSelector(getBuilding,
        building => building ? building.credentialsOriginal : []
    );

export const isBuildingCredentialsDirty =
    createSelector(
        getBuildingCredentials,
        getBuildingCredentialsOriginal,
        (current, original) => current && original && current !== original
    );

export const isSavingBuildingCredentials =
    createSelector(getBuilding,
        building => !!(building && building.savingCredentials)
    );

export const getBuildingEvents =
    createSelector(getBuilding,
        building => (building && building.events) || null
    );

export const getBuildingEventsError =
    createSelector(getBuilding,
        building => (building && building.eventsError) || null
    );

export const getBuildingHasMoreEvents =
    createSelector(getBuilding,
        building => (building && building.hasMoreEvents) || false
    );

export const getDeviceEvents =
    createSelector(
        getBuildingEvents,
        getDeviceId,
        (events, deviceId) => {
            if (!events) return {};
            if (!deviceId) return {};
            return pickBy(events, e => e.deviceId === deviceId);
        }
    );

export const getBuildingBroadcastMessage =
    createSelector(getBuilding,
        building => (building && building.broadcastMessage) || null
    );
    
export const isSettingBuildingBroadcast =
    createSelector(getBuilding,
        building => !!(building && building.settingBroadcast)
    );

export const getBuildingBroadcastError =
    createSelector(getBuilding,
        building => (building && building.settingBroadcastError) || null
    );
